import { Controller } from 'stimulus';
import Cropper from 'cropperjs';

export default class extends Controller {
  static get targets() {
    return [
      'container',
      'finalImage',
      'image',
      'newImage',
      'cropX',
      'cropY',
      'cropWidth',
      'cropHeight',
      'removeButton',
      'placeholder',
      'previewContainer',
      'closeButton',
      'cancelButton'
    ];
  }

  connect() {
    if (!this.loaded) {
      if (document.querySelector('.hidden-after-load')) {
        document.querySelector('.hidden-after-load').classList.add('hidden');
        document.querySelector('.hidden-after-load').classList.remove('opacity-0');
      }
      this.loaded = true;
    }
  }

  start() {
    const _this = this;
    _this.cropXTarget.dataset.originalValue = this.cropXTarget.value;
    _this.cropYTarget.dataset.originalValue = this.cropYTarget.value;
    _this.cropWidthTarget.dataset.originalValue = this.cropWidthTarget.value;
    _this.cropHeightTarget.dataset.originalValue = this.cropHeightTarget.value;
    _this.originalImageUrl = _this.imageTarget.src;
    _this.originalData = {
      x: +_this.cropXTarget.value,
      y: +_this.cropYTarget.value,
      width: +_this.cropWidthTarget.value,
      height: +_this.cropHeightTarget.value,
    };
    _this.originalFinalImageTarget = this.finalImageTarget;
    _this.isSaving = false;
    if (!_this.cropper) {
      _this.cropper = new Cropper(_this.imageTarget, {
        aspectRatio: 4 / 5,
        viewMode: 4 / 5,
        minContainerWidth: this.containerTarget.offsetWidth,
        minContainerHeight: 400,
        autoCropArea: 1,
        dragMode: 'move',
        restore: false,
        guides: true,
        center: false,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        ready() {
          if (_this.originalData.width && _this.originalData.height) {
            _this.cropper.setData(_this.originalData);
            _this.cropXTarget.value = _this.originalData.x;
            _this.cropYTarget.value = _this.originalData.y;
            _this.cropWidthTarget.value = _this.originalData.width;
            _this.cropHeightTarget.value = _this.originalData.height;
          } else {
            _this.cropper.setCropBoxData({
              left: "50%",
              top: 0,
            });
          }
        },
        crop(event) {
          _this.cropXTarget.value = event.detail.x;
          _this.cropYTarget.value = event.detail.y;
          _this.cropWidthTarget.value = event.detail.width;
          _this.cropHeightTarget.value = event.detail.height;
        },
      });

      this.newImageTarget.onchange = (event) => {
        if (_this.newImageUrl) {
          URL.revokeObjectURL(_this.newImageUrl);
        }
        _this.newImageUrl = URL.createObjectURL(event.target.files[0]);
        _this.cropper.replace(_this.newImageUrl);
        // Show remove button instantly when new image is selected
        if (_this.hasRemoveButtonTarget) {
          _this.removeButtonTarget.classList.remove('hidden');
        }
      };
    }
  }

  cancel() {
    if (this.isSaving) {
      return;
    }
    if (this.newImageUrl) {
      URL.revokeObjectURL(this.newImageUrl);
    }
    if (this.originalImageUrl != this.imageTarget.src) {
      this.cropper.replace(this.originalImageUrl);
      this.newImageTarget.value = null;
    }
    this.cropper.setData(this.originalData);
  }

  continue(event) {
    event.preventDefault();
    event.stopPropagation();

    const _this = this;

    // Update crop data
    this.originalData = this.cropper.getData();

    // Create cropped canvas and update preview
    const canvas = this.cropper.getCroppedCanvas({
      height: this.finalImageTarget.height,
      width: this.finalImageTarget.width,
    });

    // Update the preview image immediately
    canvas.dataset.cropImageTarget = 'finalImage';
    canvas.classList.add('w-full', 'rounded-lg');
    this.originalFinalImageTarget.replaceWith(canvas);
    this.originalFinalImageTarget = canvas;

    // Show remove button instantly when image is updated
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.remove('hidden');
    }

    // Toggle visibility: Hide placeholder, show preview container
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add('hidden');
    }
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.remove('hidden');
      // Ensure the container is positioned relatively for the remove button
      this.previewContainerTarget.classList.add('relative');
      // Ensure the canvas (which replaced finalImage) is visible
      this.originalFinalImageTarget.classList.remove('hidden');
    }

    // Get the form
    const form = this.element.closest("form");
    if (!form) {
      console.error('Form not found');
      return;
    }

    // Create FormData
    const formData = new FormData(form);
    formData.append("continue_only", "true");

    // Get CSRF token
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content;

    // Show loading state
    const continueBtn = event.currentTarget;
    const originalBtnText = continueBtn.innerHTML;
    continueBtn.disabled = true;
    continueBtn.innerHTML = 'Saving...';
    this.closeButtonTarget.disabled = true;
    this.cancelButtonTarget.disabled = true;
    this.isSaving = true;

    // Submit via AJAX
    fetch(form.action, {
      method: "PATCH",
      body: formData,
      headers: {
        'X-CSRF-Token': csrfToken
      }
    })
      .then(async response => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          const serverMessage = data.message || 'Error saving image. Please try again.';
          throw new Error(serverMessage);
        }

        console.log('Image saved successfully');
        // ... (rest of the then block)

        // Reset button first
        continueBtn.disabled = false;
        continueBtn.innerHTML = originalBtnText;
        this.closeButtonTarget.disabled = false;
        this.cancelButtonTarget.disabled = false;
        this.isSaving = false;

        // Update original state to reflect the new saved image
        // This prevents the cancel() method (triggered by modal close) from reverting to the old image
        if (_this.newImageUrl) {
          // Update the main image source to the new blob URL
          _this.imageTarget.src = _this.newImageUrl;

          // Sync Navbar Profile Picture
          const navbarImage = document.getElementById('navbar-profile-image');
          if (navbarImage) {
            navbarImage.src = _this.newImageUrl;
          }

          // Sync HeadshotEditor React Component Preview
          const editorImage = document.getElementById('headshot-preview-main');
          if (editorImage) {
            editorImage.src = _this.newImageUrl;
          }

          // Sync all generic avatars
          document.querySelectorAll('img[alt="User avatar"]').forEach(img => {
            img.src = _this.newImageUrl;
          });

          // Sync Header Profile Picture (Legacy)
          const headerImage = document.getElementById('header-profile-image');
          if (headerImage) {
            headerImage.src = _this.newImageUrl;
            // Ensure it looks correct if it was previously a placeholder
            headerImage.classList.remove('object-contain');
            headerImage.classList.add('object-cover');
            headerImage.style.backgroundColor = '';
          }

          // Dispatch event for React components
          const event = new CustomEvent('profile-image-updated', { detail: { url: _this.newImageUrl } });
          window.dispatchEvent(event);

          // Promote new image to be the "original" for the next session
          _this.originalImageUrl = _this.newImageUrl;
          // clear newImageUrl so cancel methods don't revoke it
          _this.newImageUrl = null;
          // Clear file input
          _this.newImageTarget.value = null;
        }

        // Close modal by clicking the existing cancel button
        // This button already has: data-action="click->crop-image#cancel click->modal#close"
        const cancelButton = _this.element.querySelector('[data-action*="crop-image#cancel"][data-action*="modal#close"]');

        if (cancelButton) {
          console.log(' Closing modal via cancel button');
          // Small delay to ensure state is updated
          setTimeout(() => {
            cancelButton.click();
          }, 100);
        } else {
          console.error(' Cancel button not found');
          // Fallback: hide modal manually
          const modalContainer = document.querySelector('[data-modal-target="container"]');
          if (modalContainer) {
            modalContainer.classList.add('hidden');
            modalContainer.style.display = 'none';
          }
        }
      })
      .catch(error => {
        console.error(' Error saving image:', error);
        const errorMessage = error.message || 'Error saving image. Please try again.';
        alert(errorMessage);

        // Reset button state
        continueBtn.disabled = false;
        continueBtn.innerHTML = originalBtnText;
      });
  }

  preventSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  disconnect() {
    // Clean up cropper instance when controller disconnects
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
    if (this.newImageUrl) {
      URL.revokeObjectURL(this.newImageUrl);
    }
  }
}
